import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { BackgroundVeil } from "../BackgroundVeil";
import { CenterToWindow } from "../CenterToWindow";
import { keyCodes } from "../defines/keyCodes";
import { Pictogram } from "../Pictogram";
import { Transition } from "../Transition";
import styles from "./Interstitial.module.scss";

const ANIMATION_DURATION = 12;
const CLOUD_SCALE = 0.6;
const CLOUD_SCALE_LARGE = 1.1;
const MAX_SKY_POSITION = 1100;
const MIN_SKY_POSITION = -100;
const PLANE_SCALE = 0.75;
const PLANE_SCALE_LARGE = 1.1;
const PLANE_Y_OFFSET = -15;
const PLANE_Y_OFFSET_LARGE = -30;
const SECOND = 1000;

/**
 * Interstitial provides an animation in the center of the window. The animated illustration
 * (pictogram) can be rendered after an optional delay. Also, a background veil can be rendered
 * behind the pictogram.
 */
export const Interstitial = (props) => {
  const { backgroundVeil = false, className, delay = 1.5, name } = props;
  const { screenSize } = useDeviceInfo();
  const [on, setOn] = useState(false);
  const ref = useRef();
  const renderers = {
    "jetstream-cloud": renderJetstreamCloud,
    "jetstream-sky": renderJetstreamSky,
  };
  const component = useMemo(
    () => (renderers[name] ? renderers[name]() : <Pictogram name={name} />),
    [name]
  );
  let timer = null;

  useEffect(() => {
    window.document.body.addEventListener("keydown", handleKeyDown);

    return () => window.document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    timer = setTimeout(() => setOn(true), delay * SECOND);

    return () => clearTimeout(timer);
  }, []);

  return on ? component : null;

  function renderJetstreamCloud() {
    return (
      <CenterToWindow {...getProps()}>
        {renderVeil()}
        <Transition {...getTransitionProps(styles.transitionContainer)}>
          <Pictogram {...getJetStreamCloudCloudProps()} />
          <Pictogram {...getJetStreamCloudPlaneProps()} />
        </Transition>
      </CenterToWindow>
    );
  }

  function renderJetstreamSky() {
    return (
      <CenterToWindow {...getProps()}>
        {renderVeil()}
        <Transition {...getTransitionProps(styles.transitionContainer)}>
          <div className={styles.container}>
            <Transition {...getJetStreamSkyProps()} />
          </div>
          <Pictogram {...getJetStreamPlaneProps()} />
        </Transition>
      </CenterToWindow>
    );
  }

  function renderVeil() {
    return backgroundVeil ? (
      <Transition {...getTransitionProps()}>
        <BackgroundVeil on />
      </Transition>
    ) : null;
  }

  function getProps() {
    return {
      fixed: true,
      location: "center",
      overlayClassName: classNames(styles.interstitial, className),
    };
  }

  function getTransitionProps(transitionClassName) {
    return {
      animate: { opacity: 1 },
      "aria-label": props["aria-label"],
      className: transitionClassName,
      initial: { opacity: 0 },
      onTransformationEnd: handleTransformationEnd,
      ref,
      role: "contentinfo",
      tabIndex: 0,
      transition: { ease: "easeIn" },
    };
  }

  function getJetStreamCloudCloudProps() {
    return {
      className: styles.pictogram,
      name: "cloud",
      style: { scale: screenSize === "small" ? CLOUD_SCALE : CLOUD_SCALE_LARGE },
    };
  }

  function getJetStreamCloudPlaneProps() {
    return {
      className: styles.pictogram,
      initial:
        screenSize === "small"
          ? { scale: PLANE_SCALE, y: PLANE_Y_OFFSET }
          : { scale: PLANE_SCALE_LARGE, y: PLANE_Y_OFFSET_LARGE },
      name: "jetstream",
    };
  }

  function getJetStreamSkyProps() {
    return {
      animate: { backgroundPosition: [`0px ${MAX_SKY_POSITION}px`, `0px ${MIN_SKY_POSITION}px`] },
      className: styles.backgroundClouds,
      initial: { backgroundPosition: "0px 0px" },
      transition: { duration: ANIMATION_DURATION, ease: "linear", repeat: Infinity },
    };
  }

  function getJetStreamPlaneProps() {
    return {
      className: styles.pictogram,
      initial: { scale: screenSize === "small" ? CLOUD_SCALE : CLOUD_SCALE_LARGE },
      name: "jetstream",
    };
  }

  function handleKeyDown(event) {
    event.key === keyCodes.KEY_ESCAPE && setOn(false);
  }

  function handleTransformationEnd() {
    ref.current.focus();
  }
};

Interstitial.propTypes = {
  /**
   * Text that will be read by screen readers when the interstitial is rendered. This should be
   * something like: "Loading page. Please wait."
   */
  "aria-label": PropTypes.string.isRequired,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Delay, in seconds, before interstitial will be rendered. */
  delay: PropTypes.number,

  /** Name to specify which interstitial will be rendered. */
  name: PropTypes.string.isRequired,
};
